import { useFrame, useThree } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import { AdditiveBlending, BufferAttribute, BufferGeometry, Color, type Group as GroupType } from 'three';
import { glowTexture } from './planetSkin.js';
import { prefersReducedMotion } from '../styles/tokens.js';

/**
 * The sky behind the system.
 *
 * Real starfields are not a spray of identical dots: most stars are faint,
 * a few are bright, and they are not all white — so size, brightness and
 * colour all vary, weighted so the bright ones stay rare enough to be worth
 * looking at. The whole field is one draw call.
 */
const RADIUS = 60;

/**
 * Three layers rather than one. A points material takes a single size for
 * every point it draws, so varying a per-star size attribute would mean a
 * custom shader; three draw calls at different sizes gives the same read for a
 * fraction of the complexity.
 */
const LAYERS = [
  { count: 900, size: 0.5, floor: 0.16, span: 0.3 }, // the faint many
  { count: 380, size: 0.95, floor: 0.4, span: 0.4 },
  { count: 90, size: 1.9, floor: 0.7, span: 0.3 }, // the few worth noticing
];

function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Star colours run blue-white to amber, with white much the commonest. */
const TINTS = ['#cfe0ff', '#eaf1ff', '#ffffff', '#ffffff', '#fff3dc', '#ffd9b0'];

function layerGeometry(
  seed: number,
  { count, floor, span }: { count: number; floor: number; span: number },
): BufferGeometry {
  {
    const next = rng(seed);
    const positions = new Float32Array(count * 3);
    const colours = new Float32Array(count * 3);
    const tint = new Color();

    for (let i = 0; i < count; i += 1) {
      // Even over the sphere, rather than clumped at the poles.
      const u = next() * 2 - 1;
      const angle = next() * Math.PI * 2;
      const r = Math.sqrt(1 - u * u);
      const distance = RADIUS * (0.75 + next() * 0.25);
      positions[i * 3] = Math.cos(angle) * r * distance;
      positions[i * 3 + 1] = u * distance;
      positions[i * 3 + 2] = Math.sin(angle) * r * distance;

      // Brightness on a curve inside the layer's band, so even the faint
      // layer is not a flat wash of identical dots.
      const brightness = floor + Math.pow(next(), 1.8) * span;
      tint.set(TINTS[Math.floor(next() * TINTS.length)] as string);
      colours[i * 3] = tint.r * brightness;
      colours[i * 3 + 1] = tint.g * brightness;
      colours[i * 3 + 2] = tint.b * brightness;
    }

    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(positions, 3));
    geo.setAttribute('color', new BufferAttribute(colours, 3));
    return geo;
  }
}

export function Starfield({ seed = 20260917 }: { seed?: number }) {
  const layers = useMemo(
    () => LAYERS.map((layer, i) => ({ ...layer, geometry: layerGeometry(seed + i * 7919, layer) })),
    [seed],
  );

  const sprite = useMemo(() => glowTexture(), []);
  const sky = useRef<GroupType>(null);
  const still = prefersReducedMotion();
  const { invalidate } = useThree();

  useFrame((state) => {
    if (still || !sky.current) return;
    // A very slow drift, well under conscious notice, so the sky feels alive
    // without ever pulling the eye off the planets.
    sky.current.rotation.y = state.clock.elapsedTime * 0.004;
    invalidate();
  });

  return (
    <group ref={sky}>
      {layers.map((layer, i) => (
        <points key={i} geometry={layer.geometry} frustumCulled={false}>
          <pointsMaterial
            vertexColors
            map={sprite}
            size={layer.size}
            sizeAttenuation
            transparent
            depthWrite={false}
            blending={AdditiveBlending}
          />
        </points>
      ))}
    </group>
  );
}
